import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from '../http/api.service';
import { Notification, NotificationStatus, NotificationType } from '../../models/notification.model';
import { ToastService } from './toast.service';
import { AuthService } from '../auth/services/auth.service';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private notifications = new BehaviorSubject<Notification[]>([]);
  private unreadCount = new BehaviorSubject<number>(0);

  notifications$ = this.notifications.asObservable();
  unreadCount$ = this.unreadCount.asObservable();

  constructor(private api: ApiService, private toastService: ToastService, private authService: AuthService) {
    // this.loadNotifications();
  }

  private loadNotifications(): void {
    this.api.get<Notification[]>('notifications').subscribe(notifications => {
      this.notifications.next(notifications);
      this.updateUnreadCount(notifications);
    });
  }

  private updateUnreadCount(notifications: Notification[]): void {
    const unread = notifications.filter(n => 
      n.status === NotificationStatus.UNREAD
    ).length;
    this.unreadCount.next(unread);
  }

  getNotifications(): Observable<Notification[]> {
    return this.notifications$;
  }

  markAsRead(notificationId: string): Observable<void> {
    return this.api.put<void>(`notifications/${notificationId}/read`, {}).pipe(
      map(() => {
        const current = this.notifications.value;
        const updated = current.map(n => 
          n.id === notificationId 
            ? { ...n, status: NotificationStatus.READ }
            : n
        );
        this.notifications.next(updated);
        this.updateUnreadCount(updated);
      })
    );
  }

  markAllAsRead(): Observable<void> {
    return this.api.put<void>('notifications/read-all', {}).pipe(
      map(() => {
        const current = this.notifications.value;
        const updated = current.map(n => ({
          ...n,
          status: NotificationStatus.READ
        }));
        this.notifications.next(updated);
        this.unreadCount.next(0);
      })
    );
  }

  deleteNotification(notificationId: string): Observable<void> {
    return this.api.delete<void>(`notifications/${notificationId}`).pipe(
      map(() => {
        const current = this.notifications.value;
        const updated = current.filter(n => n.id !== notificationId);
        this.notifications.next(updated);
        this.updateUnreadCount(updated);
      })
    );
  }

  notify(notification: Notification): void {
    const current = this.notifications.value;
    this.notifications.next([...current, notification]);
  }

  notifyInfo(title: string, message: string, data?: any): void {
    const newNotification: Notification = {
      id: Date.now().toString(),
      userId: this.authService.currentUserId || '',
      status: NotificationStatus.UNREAD,
      title,
      message,
      data: data || {},
      createdAt: new Date(),
      type: NotificationType.USER
    };
    this.notify(newNotification);
    this.toastService.showInfo(title, message);
  }
  
  notifyError(title: string, message: string, data?: any): void {
    const newNotification: Notification = {
      id: Date.now().toString(),
      userId: this.authService.currentUserId || '',
      status: NotificationStatus.UNREAD,
      title,
      message,
      data: data || {},
      createdAt: new Date(),
      type: NotificationType.USER
    };
    this.notify(newNotification);
    this.toastService.showError(title, message);
  }
  
  notifyWarning(title: string, message: string, data?: any): void {
    const newNotification: Notification = {
      id: Date.now().toString(),
      userId: this.authService.currentUserId || '',
      status: NotificationStatus.UNREAD,
      title,
      message,
      data: data || {},
      createdAt: new Date(),
      type: NotificationType.USER
    };
    this.notify(newNotification);
    this.toastService.showWarning(title, message);
  }
  
  notifySuccess(title: string, message: string, data?: any): void {
    const newNotification: Notification = {
      id: Date.now().toString(),
      userId: this.authService.currentUserId || '',
      status: NotificationStatus.UNREAD,
      title,
      message,
      data: data || {},
      createdAt: new Date(),
      type: NotificationType.USER
    };
    this.notify(newNotification);
    this.toastService.showSuccess(title, message);
  }

  clearAll(): void {
    this.notifications.next([]);
    this.unreadCount.next(0);
  }

  getUnreadCount(): number {
    return this.unreadCount.value;
  }

  toggleRead(notificationId: string): void {
    const current = this.notifications.value;
    const updated = current.map(n => n.id === notificationId ? { ...n, status: n.status === NotificationStatus.READ ? NotificationStatus.UNREAD : NotificationStatus.READ } : n);
    this.notifications.next(updated);
    this.updateUnreadCount(updated);
  }

  removeNotification(notificationId: string): void {
    const current = this.notifications.value;
    const updated = current.filter(n => n.id !== notificationId);
    this.notifications.next(updated);
    this.updateUnreadCount(updated);
  }

}
